import { useQuery, useQueryClient } from '@tanstack/react-query';

import { REFERRAL_INTAKE_API_NAME } from 'components/inboundReferrals/constants';
import { useProfile } from 'context/profile';
import useMutateWithToast from 'hooks/useMutateWithToast';
import useWorkflowNudge from 'hooks/useWorkflowNudge';
import { PERMISSIONS } from 'models/Profile';
import Workflow from 'models/Workflow';
import { indexTours } from 'services/api/tours';
import { validationErrorMessage } from 'services/api/validationErrors';
import { workflowQueryKeys } from 'services/api/workflows';

/**
 * The referral's tours, read by the Tour Tracking card.
 * Tours are writable on any referral that isn't archived, locked or a confirmed duplicate, finished
 * ones included. `canEdit` doesn't go through useWorkflowPermissions' canEditAction, which answers
 * from referral-edit permissions rather than tour edit.
 */
export default function useWorkflowTours(workflow: Workflow) {
  const { profile } = useProfile();
  const enabled = !!profile.tourTrackingEnabled && workflow.definition.apiName === REFERRAL_INTAKE_API_NAME;

  const query = useQuery({
    queryKey: workflowQueryKeys.tours(workflow.id),
    queryFn: ({ signal }) => indexTours(workflow.id, signal),
    enabled,
  });
  useWorkflowNudge(enabled ? workflow.id : undefined, workflowQueryKeys.tours(workflow.id));

  return {
    enabled,
    canEdit:
      enabled &&
      !!profile.has(PERMISSIONS.tourEdit) &&
      !workflow.isArchived &&
      !workflow.isLocked &&
      !workflow.isConfirmedDuplicate,
    isLoaded: query.isSuccess,
    tours: query.data?.tours ?? [],
    groupIds: query.data?.groupIds ?? [],
  };
}

export function useTourMutation<T>({
  workflowId,
  mutationFn,
  onSuccess,
}: {
  workflowId: string;
  mutationFn: (variables: T) => Promise<unknown>;
  onSuccess: () => void;
}) {
  const queryClient = useQueryClient();

  return useMutateWithToast({
    mutationFn,
    errorToast: validationErrorMessage,
    // A refusal can mean the referral finished or locked without a live update (an admission stamped
    // by a bare update_all), so refresh what decides whether the tour controls show.
    onError: () => {
      queryClient.invalidateQueries({ queryKey: workflowQueryKeys.show(workflowId) });
      queryClient.invalidateQueries({ queryKey: workflowQueryKeys.tours(workflowId) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowQueryKeys.tours(workflowId) });
      queryClient.invalidateQueries({ queryKey: workflowQueryKeys.logs(workflowId) });
      onSuccess();
    },
  });
}
